import { Heading, Image, useBreakpointValue } from "@chakra-ui/react";
import type { AirlineRankingTemplateProps } from "../AirHelpScoreWrapper";
import { AirHelpScoreWrapper } from "../AirHelpScoreWrapper";
import { Hero, HowScoreIsCalculated, JoinTheCommunity, ScoreTag, Share, TopThree } from "../common";
import { BestRated } from "../common/BestRated/BestRated";
import { ContentMedia } from "../common/ContentMedia";
import TopThreeCard from "../common/TopThree/TopThreeCard";
import { contactSectionMediaStyles, globalRankingBgStyles } from "../styles";
import { getStoryblokAsset } from "../utils";
import AirlineRankingTable from "./AirlineRankingTable";
import columns from "./AirlineRankingTable/config";

export const AirlineRanking = ({
  story,
  cmsLanguagesData,
  serverSideStoryblokPages,
  airlinesScores,
  airlinesScoresByCountry,
  airlinesRegions,
  whiteListDomains,
  nofollowUrls,
  globalComponents,
  optimizelyExperiments,
}: AirlineRankingTemplateProps) => {
  const topThreeAirlines = airlinesScores.slice(0, 3);

  const {
    content: {
      heroHeading,
      heroSubHeading,
      heroLink,
      heroText,
      howIsItCalculatedHeading,
      howIsItCalculatedFirstItemHeading,
      howIsItCalculatedFirstItemIcon,
      howIsItCalculatedFirstItemText,
      howIsItCalculatedSecondItemHeading,
      howIsItCalculatedSecondItemIcon,
      howIsItCalculatedSecondItemText,
      howIsItCalculatedThirdItemHeading,
      howIsItCalculatedThirdItemIcon,
      howIsItCalculatedThirdItemText,
      globalRankingText,
      aboutText,
      contactContent,
      contactImage,
      globalRankingImageDesktop,
      globalRankingImageTablet,
      globalRankingImageMobile,
      aboutMedia,
      heroImageDesktop,
      heroImageTablet,
      heroImageMobile,
      bestAirlinesFirstLogo,
      bestAirlinesSecondLogo,
      bestAirlinesThirdLogo,
      topRatedHeading,
    },
  } = story;

  const topThreeAirlinesLogos = [bestAirlinesFirstLogo, bestAirlinesSecondLogo, bestAirlinesThirdLogo];

  const howIsItCalculatedItems = [
    {
      heading: howIsItCalculatedFirstItemHeading,
      icon: howIsItCalculatedFirstItemIcon,
      text: howIsItCalculatedFirstItemText,
    },
    {
      heading: howIsItCalculatedSecondItemHeading,
      icon: howIsItCalculatedSecondItemIcon,
      text: howIsItCalculatedSecondItemText,
    },
    {
      heading: howIsItCalculatedThirdItemHeading,
      icon: howIsItCalculatedThirdItemIcon,
      text: howIsItCalculatedThirdItemText,
    },
  ];

  const responsiveImages = useBreakpointValue(
    {
      base: { heroImage: heroImageMobile, globalRankingImage: globalRankingImageMobile },
      md: { heroImage: heroImageTablet, globalRankingImage: globalRankingImageTablet },
      xl: { heroImage: heroImageDesktop, globalRankingImage: globalRankingImageDesktop },
    },
    { ssr: true }
  );
  return (
    <AirHelpScoreWrapper
      story={story}
      cmsLanguagesData={cmsLanguagesData}
      serverSideStoryblokPages={serverSideStoryblokPages}
      whiteListDomains={whiteListDomains}
      nofollowUrls={nofollowUrls}
      globalComponents={globalComponents}
      optimizelyExperiments={optimizelyExperiments}
    >
      <Hero
        heading={heroHeading}
        subHeading={heroSubHeading}
        text={heroText}
        link={heroLink}
        heroImage={responsiveImages?.heroImage}
      />
      <TopThree heading={topRatedHeading}>
        {topThreeAirlines.map((item, index) => {
          const { airline, airline_code: airlineCode, score } = item;

          return (
            <TopThreeCard key={airlineCode}>
              <Image
                src={getStoryblokAsset(topThreeAirlinesLogos[index]).assetSource}
                alt={airline}
                margin="auto"
                maxH={14}
                mb={6}
              />
              <Heading variant="h5" as="h5" mb={4}>
                {airline}
              </Heading>
              <ScoreTag index={index} score={score} size="lg" />
            </TopThreeCard>
          );
        })}
      </TopThree>
      <AirlineRankingTable columns={columns} rows={airlinesScores} heading="global_airline_ranking" />
      <BestRated bestByCountry={airlinesScoresByCountry} bestByRegion={airlinesRegions} scoresType="airlines" />
      <HowScoreIsCalculated items={howIsItCalculatedItems} heading={howIsItCalculatedHeading} />
      <ContentMedia
        content={globalRankingText}
        storyblokAsset={responsiveImages?.globalRankingImage}
        bgImage={globalRankingBgStyles}
      />
      <ContentMedia content={aboutText} storyblokAsset={aboutMedia} mediaPosition="right" />
      <ContentMedia
        content={contactContent}
        storyblokAsset={contactImage}
        borderTop="1px solid #F3F6F9"
        mediaStyles={contactSectionMediaStyles}
      />
      <JoinTheCommunity />
      <Share />
    </AirHelpScoreWrapper>
  );
};
